import { useEffect, useRef, useState, useCallback } from 'react';
import { useT } from '../i18n';
import {
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  type NotificationItem,
} from '../services/api';

// Issue #281: Sino de notificacoes no header com dropdown e polling 30s.
// So renderiza para usuarios autenticados (Layout so monta quando user existe).
//
// Ao clicar em uma notificacao ela e marcada como LIDA mas PERMANECE na lista,
// apenas mudando o destaque visual (lida = cinza sem negrito, nao lida =
// preta negrito). As notificacoes lidas so sao removidas pelo purge diario
// no backend (app/core/notification_cleanup.py).

function timeAgo(iso: string | null, t: (k: string) => string): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Math.max(0, Date.now() - then);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return t('notifications.justNow');
  const min = Math.floor(sec / 60);
  if (min < 60) return t('notifications.minutesAgo').replace('{n}', String(min));
  const hr = Math.floor(min / 60);
  if (hr < 24) return t('notifications.hoursAgo').replace('{n}', String(hr));
  const day = Math.floor(hr / 24);
  return t('notifications.daysAgo').replace('{n}', String(day));
}

export default function Notifications() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const refresh = useCallback(async () => {
    try {
      const resp = await getNotifications();
      setItems(resp.notifications);
      setUnread(resp.unread_count);
    } catch {
      // Silencioso: 401 e tratado globalmente pelo cliente HTTP (Layout).
    }
  }, []);

  // Polling 30s: atualiza a lista/contador de notificacoes.
  useEffect(() => {
    refresh();
    const id = window.setInterval(refresh, 30000);
    return () => window.clearInterval(id);
  }, [refresh]);

  // Fecha o dropdown ao clicar fora.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Ao clicar: marca como lida via API mas NAO remove da lista — apenas
  // atualiza o destaque visual (is_read = true).
  async function handleClickNotif(n: NotificationItem) {
    if (n.is_read) return;
    // Otimista: atualiza o destaque imediatamente.
    setItems((prev) =>
      prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)),
    );
    setUnread((u) => Math.max(0, u - 1));
    try {
      await markNotificationRead(n.id);
    } catch {
      // Reverte em caso de erro.
      setItems((prev) =>
        prev.map((x) => (x.id === n.id ? { ...x, is_read: false } : x)),
      );
      setUnread((u) => u + 1);
    }
  }

  // Marcar todas como lidas: mantem na lista, apenas muda o destaque.
  async function handleMarkAll() {
    setLoading(true);
    setItems((prev) => prev.map((x) => ({ ...x, is_read: true })));
    const prevUnread = unread;
    setUnread(0);
    try {
      await markAllNotificationsRead();
    } catch {
      // Reverte em caso de erro.
      setUnread(prevUnread);
      await refresh();
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={t('notifications.title')}
        className="relative inline-flex items-center justify-center rounded-md p-1.5 text-brand-600 hover:bg-brand-50 hover:text-brand-900 focus:outline-none focus:ring-2 focus:ring-brand-400"
      >
        {/* sino */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-5 w-5"
          aria-hidden="true"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold leading-none text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border border-brand-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-brand-100 px-4 py-2.5">
            <span className="text-sm font-semibold text-brand-800">
              {t('notifications.title')}
            </span>
            {unread > 0 && (
              <button
                type="button"
                onClick={handleMarkAll}
                disabled={loading}
                className="text-xs font-medium text-brand-600 hover:text-brand-900 disabled:opacity-50"
              >
                {t('notifications.markAllRead')}
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-brand-400">
                {t('notifications.empty')}
              </div>
            ) : (
              <ul className="divide-y divide-brand-50">
                {items.map((n) => {
                  // Nao lida: preta negrito. Lida: cinza sem negrito.
                  const isUnread = !n.is_read;
                  return (
                    <li key={n.id}>
                      <button
                        type="button"
                        onClick={() => handleClickNotif(n)}
                        className={
                          'flex w-full flex-col gap-0.5 px-4 py-3 text-left hover:bg-brand-50 ' +
                          (isUnread
                            ? 'bg-brand-50/40'
                            : 'bg-transparent')
                        }
                      >
                        <span
                          className={
                            'text-sm ' +
                            (isUnread
                              ? 'font-bold text-gray-900'
                              : 'font-normal text-gray-400')
                          }
                        >
                          {n.title}
                        </span>
                        {n.body && (
                          <span
                            className={
                              'text-xs ' +
                              (isUnread ? 'text-brand-600' : 'text-gray-400')
                            }
                          >
                            {n.body}
                          </span>
                        )}
                        <span className="text-[11px] text-brand-400">
                          {timeAgo(n.created_at, t)}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
